<div class="preview-container">
  <!-- Loading State -->
  <div *ngIf="isLoading" class="no-data-container">
    <div class="no-data-card">
      <i class="pi pi-spin pi-spinner text-6xl text-primary"></i>
      <h3>Cargando detalles de la campaña...</h3>
      <p>Por favor espera un momento</p>
    </div>
  </div>

  <!-- Error State -->
  <div *ngIf="hasError && !isLoading" class="no-data-container">
    <div class="no-data-card">
      <i class="pi pi-exclamation-triangle text-6xl text-red-500"></i>
      <h3>Error al cargar la campaña</h3>
      <p>{{ errorMessage }}</p>
      <button pButton label="Volver" icon="pi pi-arrow-left" (click)="goBack()" class="mt-3"></button>
    </div>
  </div>

  <!-- Content -->
  <div *ngIf="!isLoading && !hasError && campaign">
    <!-- Header -->
    <div class="preview-header">
      <div class="flex justify-content-between align-items-center">
        <div class="header-title">
          <h1>
            <i class="fa-solid fa-bullseye mr-2"></i>
            Detalle de Campaña
          </h1>
        </div>
        <div class="header-actions">
          <button 
            pButton 
            label="Volver" 
            icon="pi pi-arrow-left" 
            class="p-button-outlined p-button-secondary"
            (click)="goBack()"
          ></button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="preview-content">
      <!-- Cards Grid: Estado y Resumen -->
      <div class="cards-grid">
        <!-- Estado de la Campaña -->
        <div class="info-card">
          <div class="card-header">
            <i class="fa-solid fa-info-circle text-blue-600"></i>
            <h3>Estado de la Campaña</h3>
            <div class="header-tags">
              <p-tag 
                [value]="getStatusLabel(campaign.status)" 
                [severity]="getStatusColor(campaign.status)"
              ></p-tag>
            </div>
          </div>
          <div class="card-content">
            <!-- Información del Estado -->
            <div class="status-info-section">
              <div class="status-indicator" [style.background-color]="getStatusInfo(campaign.status).color">
                <i [class]="getStatusInfo(campaign.status).icon"></i>
              </div>
              <div class="status-details">
                <h4 class="status-title">{{ getStatusInfo(campaign.status).label }}</h4>
                <p class="status-description">{{ getStatusInfo(campaign.status).description }}</p>
              </div>
            </div>

            <!-- Fechas -->
            <div class="campaign-dates">
              <div class="date-item">
                <i class="fa-solid fa-calendar-days text-purple-600"></i>
                <div class="date-content">
                  <span class="date-label">Programada</span>
                  <span class="date-value">{{ formatDateShort(campaign.scheduledDate) }}</span>
                </div>
              </div>
              <div class="date-item" *ngIf="campaign.executionDate">
                <i class="fa-solid fa-calendar-check text-green-600"></i>
                <div class="date-content">
                  <span class="date-label">Ejecutada</span>
                  <span class="date-value">{{ formatDateShort(campaign.executionDate) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Información General -->
        <div class="info-card highlight-card">
          <div class="card-header">
            <i class="fa-solid fa-file-alt text-green-600"></i>
            <h3>Información General</h3>
          </div>
          <div class="card-content">
            <div class="info-grid">
              <div class="info-row">
                <span class="info-label">
                  <i class="fa-solid fa-hashtag"></i>
                  ID de Campaña
                </span>
                <span class="info-value mono">{{ campaign._id }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">
                  <i class="fa-solid fa-chess-knight"></i>
                  Estrategia
                </span>
                <span class="info-value">{{ campaign.strategyId?.name || 'N/A' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">
                  <i class="fa-solid fa-file-contract"></i>
                  Lease
                </span>
                <span class="info-value mono">{{ campaign.leaseId?.folio || 'N/A' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">
                  <i class="fa-solid fa-user"></i>
                  Cliente
                </span>
                <span class="info-value">{{ campaign.clientId?.name || 'N/A' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Estrategia Detail Card -->
      <div class="info-card wide-card">
        <div class="card-header">
          <i class="fa-solid fa-chess-queen text-purple-600"></i>
          <h3>Detalles de la Estrategia</h3>
          <div class="header-tags">
            <p-tag 
              [value]="campaign.strategyId?.scenario || 'N/A'" 
              severity="info"
              icon="fa-solid fa-sitemap"
            ></p-tag>
            <p-tag 
              *ngIf="campaign.strategyId?.autoExecute"
              value="Auto-Ejecutar" 
              severity="success"
              icon="fa-solid fa-bolt"
            ></p-tag>
          </div>
        </div>
        <div class="card-content">
          <div class="strategy-description" *ngIf="campaign.strategyId?.description">
            <i class="fa-solid fa-align-left text-gray-500"></i>
            <p>{{ campaign.strategyId.description }}</p>
          </div>

          <div class="strategy-config">
            <div class="config-item">
              <i class="fa-solid fa-calendar-minus text-orange-600"></i>
              <div>
                <span class="config-label">Días de Offset</span>
                <span class="config-value">{{ campaign.strategyId?.daysOffset }} días</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Canales de Comunicación Card -->
      <div class="info-card wide-card">
        <div class="card-header">
          <i class="fa-solid fa-tower-broadcast text-blue-600"></i>
          <h3>Canales de Comunicación</h3>
          <div class="header-tags">
            <p-tag 
              [value]="campaign.channels?.length + ' canal(es)'" 
              severity="info"
            ></p-tag>
          </div>
        </div>
        <div class="card-content">
          <div class="channels-grid">
            <!-- Email -->
            <div class="channel-card" [class.channel-enabled]="campaign.strategyId?.channels?.email?.enabled">
              <div class="channel-header">
                <i class="fa-solid fa-envelope"></i>
                <span class="channel-name">Email</span>
                <p-tag 
                  [value]="campaign.strategyId?.channels?.email?.enabled ? 'Habilitado' : 'Deshabilitado'"
                  [severity]="campaign.strategyId?.channels?.email?.enabled ? 'success' : 'secondary'"
                ></p-tag>
              </div>
              <div class="channel-details" *ngIf="campaign.strategyId?.channels?.email?.enabled">
                <div class="detail-row">
                  <span class="detail-label">Prioridad:</span>
                  <span class="detail-value">{{ campaign.strategyId.channels.email.priority }}</span>
                </div>
                <div class="detail-row" *ngIf="getChannelExecutionTime('email')">
                  <span class="detail-label">
                    <i class="fa-solid fa-clock mr-1"></i>
                    Hora de envío:
                  </span>
                  <span class="detail-value">{{ getChannelExecutionTime('email') }}</span>
                </div>
              </div>
            </div>

            <!-- SMS -->
            <div class="channel-card" [class.channel-enabled]="campaign.strategyId?.channels?.sms?.enabled">
              <div class="channel-header">
                <i class="fa-solid fa-comment-sms"></i>
                <span class="channel-name">SMS</span>
                <p-tag 
                  [value]="campaign.strategyId?.channels?.sms?.enabled ? 'Habilitado' : 'Deshabilitado'"
                  [severity]="campaign.strategyId?.channels?.sms?.enabled ? 'success' : 'secondary'"
                ></p-tag>
              </div>
              <div class="channel-details" *ngIf="campaign.strategyId?.channels?.sms?.enabled">
                <div class="detail-row">
                  <span class="detail-label">Prioridad:</span>
                  <span class="detail-value">{{ campaign.strategyId.channels.sms.priority }}</span>
                </div>
                <div class="detail-row" *ngIf="getChannelExecutionTime('sms')">
                  <span class="detail-label">
                    <i class="fa-solid fa-clock mr-1"></i>
                    Hora de envío:
                  </span>
                  <span class="detail-value">{{ getChannelExecutionTime('sms') }}</span>
                </div>
              </div>
            </div>

            <!-- WhatsApp -->
            <div class="channel-card" [class.channel-enabled]="campaign.strategyId?.channels?.whatsapp?.enabled">
              <div class="channel-header">
                <i class="fa-brands fa-whatsapp"></i>
                <span class="channel-name">WhatsApp</span>
                <p-tag 
                  [value]="campaign.strategyId?.channels?.whatsapp?.enabled ? 'Habilitado' : 'Deshabilitado'"
                  [severity]="campaign.strategyId?.channels?.whatsapp?.enabled ? 'success' : 'secondary'"
                ></p-tag>
              </div>
              <div class="channel-details" *ngIf="campaign.strategyId?.channels?.whatsapp?.enabled">
                <div class="detail-row">
                  <span class="detail-label">Prioridad:</span>
                  <span class="detail-value">{{ campaign.strategyId.channels.whatsapp.priority }}</span>
                </div>
                <div class="detail-row" *ngIf="getChannelExecutionTime('whatsapp')">
                  <span class="detail-label">
                    <i class="fa-solid fa-clock mr-1"></i>
                    Hora de envío:
                  </span>
                  <span class="detail-value">{{ getChannelExecutionTime('whatsapp') }}</span>
                </div>
              </div>
            </div>

            <!-- Call -->
            <div class="channel-card" [class.channel-enabled]="campaign.strategyId?.channels?.call?.enabled">
              <div class="channel-header">
                <i class="fa-solid fa-phone"></i>
                <span class="channel-name">Llamada</span>
                <p-tag 
                  [value]="campaign.strategyId?.channels?.call?.enabled ? 'Habilitado' : 'Deshabilitado'"
                  [severity]="campaign.strategyId?.channels?.call?.enabled ? 'success' : 'secondary'"
                ></p-tag>
              </div>
              <div class="channel-details" *ngIf="campaign.strategyId?.channels?.call?.enabled">
                <div class="detail-row">
                  <span class="detail-label">Prioridad:</span>
                  <span class="detail-value">{{ campaign.strategyId.channels.call.priority }}</span>
                </div>
                <div class="detail-row" *ngIf="getChannelExecutionTime('call')">
                  <span class="detail-label">
                    <i class="fa-solid fa-clock mr-1"></i>
                    Hora de envío:
                  </span>
                  <span class="detail-value">{{ getChannelExecutionTime('call') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Resultados de Ejecución -->
      <div class="info-card wide-card" *ngIf="campaign.results && campaign.results.length > 0">
        <div class="card-header">
          <i class="fa-solid fa-chart-bar text-green-600"></i>
          <h3>Resultados de Ejecución</h3>
          <div class="header-tags">
            <p-tag 
              [value]="campaign.results.length + ' resultado(s)'" 
              severity="success"
            ></p-tag>
          </div>
        </div>
        <div class="card-content">
          <div class="results-grid">
            <div class="result-card" *ngFor="let result of campaign.results">
              <div class="result-header">
                <i [class]="getChannelIcon(result.channel)"></i>
                <span class="result-channel">{{ result.channel }}</span>
                <i 
                  [class]="getResultIcon(result.success)"
                  [style.color]="getResultColor(result.success)"
                  class="text-2xl"
                ></i>
              </div>
              <div class="result-status">
                <p-tag 
                  [value]="result.success ? 'Exitoso' : 'Fallido'"
                  [severity]="result.success ? 'success' : 'danger'"
                ></p-tag>
              </div>
              <div class="result-message" *ngIf="result.error">
                <i class="fa-solid fa-exclamation-triangle text-orange-600"></i>
                <span>{{ result.error }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Información del Cliente y Lease -->
      <div class="cards-grid">
        <!-- Cliente -->
        <div class="info-card">
          <div class="card-header">
            <i class="fa-solid fa-user text-blue-600"></i>
            <h3>Cliente</h3>
          </div>
          <div class="card-content">
            <div class="client-info">
              <div class="client-avatar">
                <i class="fa-solid fa-user text-3xl text-blue-600"></i>
              </div>
              <div class="client-details">
                <h4 class="client-name">{{ campaign.clientId?.name || 'N/A' }}</h4>
                <div class="client-meta">
                  <div class="meta-item">
                    <i class="fa-solid fa-envelope"></i>
                    <span class="meta-value">{{ campaign.clientId?.email || 'N/A' }}</span>
                  </div>
                  <div class="meta-item">
                    <i class="fa-solid fa-phone"></i>
                    <span class="meta-value">{{ campaign.clientId?.phone || 'N/A' }}</span>
                  </div>
                  <div class="meta-item">
                    <i class="fa-solid fa-id-card"></i>
                    <span class="meta-value mono">{{ campaign.clientId?.rfc || 'N/A' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Lease -->
        <div class="info-card">
          <div class="card-header">
            <i class="fa-solid fa-file-contract text-purple-600"></i>
            <h3>Arrendamiento</h3>
          </div>
          <div class="card-content">
            <div class="lease-info">
              <div class="info-row">
                <span class="info-label">
                  <i class="fa-solid fa-hashtag"></i>
                  Folio
                </span>
                <span class="info-value mono">{{ campaign.leaseId?.folio || 'N/A' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">
                  <i class="fa-solid fa-circle-info"></i>
                  Estado
                </span>
                <p-tag 
                  [value]="campaign.leaseId?.status || 'N/A'" 
                  severity="info"
                ></p-tag>
              </div>
              <div class="info-row" *ngIf="campaign.leaseId?.startDate">
                <span class="info-label">
                  <i class="fa-solid fa-calendar-day"></i>
                  Inicio
                </span>
                <span class="info-value">{{ formatDateShort(campaign.leaseId.startDate) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Auditoría -->
      <div class="info-card wide-card">
        <div class="card-header">
          <i class="fa-solid fa-clock-rotate-left text-gray-600"></i>
          <h3>Auditoría</h3>
        </div>
        <div class="card-content">
          <div class="audit-grid">
            <div class="audit-item">
              <i class="fa-solid fa-calendar-plus text-green-600"></i>
              <div>
                <span class="audit-label">Creado</span>
                <span class="audit-value">{{ formatDate(campaign.createdAt) }}</span>
              </div>
            </div>
            <div class="audit-item">
              <i class="fa-solid fa-calendar-pen text-blue-600"></i>
              <div>
                <span class="audit-label">Actualizado</span>
                <span class="audit-value">{{ formatDate(campaign.updatedAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
